// The desktop's tool-lock posts (/api/user/tool-lock): one at a time, newest value wins.
//
// The route writes only when the stored flag differs (plan D / D4, lib/verktygslas.ts). That
// read-then-write is only correct if a client's posts do not overlap. They used to: moving
// between two locked tasks posts "false" from the old page's unmount and "true" from the new
// page's mount in the same instant, and the "true" would read the still-stored "true", skip,
// and let the "false" land — the phone's calculator unlocked on a locked task. So:
//   - calls in the same tick collapse into one post of the last value (unmount + mount);
//   - while a post is in flight, later calls only replace the value to send next, and that
//     value goes out when the post has settled (its write has committed, so the next read
//     sees it).
// One sender per page load (module state), shared by every component that sets the lock.
//
// Leaving a task page does not unlock at once (300-student run, analysis FA / F-2026-012).
// The [...slug] loading boundary unmounts the old task page in its own commit and the next
// task page mounts 0.8–1.2 s later, so "unlock on unmount" posted false, then true, on every
// navigation between two locked tasks: two singleton writes, two phone wake-ups, and the
// phone's calculator unlocked for a second on a locked task. The unmount now schedules the
// release instead (slappSenare): after SLAPP_FORDROJNING_MS the page's tools unlock locally
// AND false is posted — one owner for both, so the desktop and the phone always agree. Any
// lock call before then (synka, or a local set through ToolLockProvider) cancels it, so a
// release can never fire after the next page has locked. Leaving the task area for good
// therefore unlocks within SLAPP_FORDROJNING_MS plus one post. On pagehide (tab closed,
// full-page navigation, bfcache) a pending release is sent at once with keepalive.

export type SkickaVerktygslas = (locked: boolean) => Promise<unknown>;

export function skapaVerktygslasSynk(skicka: SkickaVerktygslas): (locked: boolean) => void {
  let nasta: boolean | null = null;
  let igang = false;

  async function tom(): Promise<void> {
    while (nasta !== null) {
      const locked = nasta;
      nasta = null;
      try {
        await skicka(locked);
      } catch {
        // Best-effort as before: the next change posts again.
      }
    }
    igang = false;
  }

  return (locked) => {
    nasta = locked;
    if (igang) return;
    igang = true;
    // A microtask, not a timer: the unmount cleanup and the mount effect run in the same
    // synchronous flush, so this sees only the last of them.
    queueMicrotask(() => void tom());
  };
}

/**
 * How long a left task page keeps the tools locked before releasing them. Above the
 * task-to-task navigation p99 at 300 students (1.45 s) plus the mount; a slower navigation
 * only degrades to the old unlock-then-relock, nothing breaks.
 */
export const SLAPP_FORDROJNING_MS = 3_000;

export interface VerktygslasKlient {
  /** Post `locked` (newest wins). Cancels a pending release first. */
  synka(locked: boolean): void;
  /** A task page is leaving: release the tools (locally and on the server) after the delay. */
  slappSenare(): void;
  /** A local lock change (ToolLockProvider's setters): cancels a pending release so its
   *  timer can never undo it. Posts nothing. */
  avbrytSlapp(): void;
  /** pagehide: a pending release is applied locally and sent now; otherwise nothing. */
  tomVidSidbyte(): void;
  /** How the page's tools are unlocked locally; ToolLockProvider registers it, and null on
   *  its unmount (which also cancels a pending release: no timer outlives the provider). */
  sattLokalSlapp(fn: (() => void) | null): void;
}

export interface Timer {
  satt(fn: () => void, ms: number): unknown;
  rensa(handtag: unknown): void;
}

const standardTimer: Timer = {
  satt: (fn, ms) => setTimeout(fn, ms),
  rensa: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export function skapaVerktygslasKlient(opts: {
  skicka: SkickaVerktygslas;
  /** Fire-and-forget post that survives the page (fetch keepalive). */
  skickaVidSidbyte: (locked: boolean) => void;
  fordrojningMs?: number;
  timer?: Timer;
}): VerktygslasKlient {
  const ko = skapaVerktygslasSynk(opts.skicka);
  const timer = opts.timer ?? standardTimer;
  const fordrojning = opts.fordrojningMs ?? SLAPP_FORDROJNING_MS;
  let vantande: { handtag: unknown } | null = null;
  let lokalSlapp: (() => void) | null = null;

  /** Clears a pending release; true if there was one. */
  function avbryt(): boolean {
    if (!vantande) return false;
    timer.rensa(vantande.handtag);
    vantande = null;
    return true;
  }

  return {
    synka(locked) {
      avbryt();
      ko(locked);
    },
    slappSenare() {
      avbryt();
      const post = {
        handtag: timer.satt(() => {
          if (vantande !== post) return;
          vantande = null;
          lokalSlapp?.();
          ko(false);
        }, fordrojning),
      };
      vantande = post;
    },
    avbrytSlapp() {
      avbryt();
    },
    tomVidSidbyte() {
      if (!avbryt()) return;
      // Locally too: a page restored from the bfcache must not show a locked calculator
      // that the server has already released.
      lokalSlapp?.();
      opts.skickaVidSidbyte(false);
    },
    sattLokalSlapp(fn) {
      lokalSlapp = fn;
      if (fn === null) avbryt();
    },
  };
}

/**
 * Bounds how long a hung post holds the queue (fetch has no timeout of its own). The abort is
 * client-side only: an aborted post may still reach the server and commit after a newer one
 * sent meanwhile — accepted, it takes a 15 s hang.
 */
const POST_TIMEOUT_MS = 15_000;

const posta = (locked: boolean, keepalive: boolean) =>
  fetch("/api/user/tool-lock", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ locked }),
    keepalive,
    signal:
      !keepalive && typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(POST_TIMEOUT_MS) : undefined,
  });

/** The page's tool-lock client (module state: one per page load). */
export const verktygslasKlient = skapaVerktygslasKlient({
  skicka: (locked) => posta(locked, false),
  // Outside the serial queue: the page is going away. A post still in flight from the task
  // page could in theory commit after it — it was sent seconds earlier, so that needs a hung post.
  skickaVidSidbyte: (locked) => void posta(locked, true).catch(() => {}),
});

/** Post the lock for the task now shown (cancels a pending release). */
export const synkaVerktygslas = (locked: boolean) => verktygslasKlient.synka(locked);

/** A task page unmounts: release its lock after SLAPP_FORDROJNING_MS unless the next page locks first. */
export const slappVerktygslasSenare = () => verktygslasKlient.slappSenare();
